import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { DocumentalEvaluationResult } from "../interfaces/document-category.interface";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

@Injectable()
export class DocumentsService {
  constructor(private http: HttpClientService) {}

  public postDocument(
    doc: any,
    type,
    doc_string,
    id,
    resource_class = "Application",
    metadata: object = null,
    create_source = undefined
  ): Observable<any> {
    const documentPayload: Record<string, unknown> = {
      category: doc.category,
      original_filename: doc.name,
      comments: "comment",
      content: doc_string,
      password: doc.password,
      metadata,
    };

    if (create_source) {
      documentPayload.create_source = create_source;
    }

    const options = {
      resource_class,
      resource_id: id,
      documents: [documentPayload],
    };
    return this.http.post("/api/documents", options).pipe(tap((_) => console.log("DocumentService.postDocument: Ok.")));
  }

  public postDocuments(documents, id, resource_class): Observable<any> {
    const options = {
      resource_class,
      resource_id: id,
      documents,
    };
    return this.http
      .post("/api/documents", options)
      .pipe(tap((_) => console.log("DocumentService.postDocuments: Ok.")));
  }

  public postDocumentWithDownpayments(
    doc: any,
    type,
    doc_string,
    id,
    resource_class = "Application",
    downpayments_options,
    metadata: object = null
  ): Observable<any> {
    const options = {
      resource_class,
      resource_id: id,
      category: doc.category,
      original_filename: doc.name,
      comments: "comment",
      content: doc_string,
      password: doc.password,
      metadata,
      document: {
        downpayments_attributes: [...downpayments_options],
      },
    };
    return this.http
      .post("/api/documents/upload_downpayments", options)
      .pipe(tap((_) => console.log("DocumentService.postDocumentWithDownpayments: Ok.")));
  }

  public getDocumentsByMetadata(id, resource_class, categories: Array<{ [key: string]: { [key: string]: any } }>) {
    const options = {
      resource_class,
      resource_id: id,
      categories,
    };
    return this.http
      .post("/api/documents/search_by_metadata", options)
      .pipe(tap((_) => console.log("DocumentService.getDocumentsByMetadata: Ok.")));
  }

  public bulkPutDocumentsMetadata(documents: Array<{ document_id; metadata }>): Observable<any> {
    const options = {
      documents,
    };
    return this.http
      .put("/api/documents/metadata", options)
      .pipe(tap((_) => console.log("DocumentService.putDocumentsMetadata: Ok.")));
  }

  public getDocuments(id: string, resource_class = "Application"): Observable<any> {
    const options = {
      params: {
        resource_class,
        resource_id: id,
      },
    };
    return this.http.get("/api/documents", options).pipe(tap((_) => console.log("DocumentService.getDocuments: Ok.")));
  }

  public updateDocuments(file): Observable<any> {
    return this.http
      .put(`/api/documents/${file.id}`, file)
      .pipe(tap((_) => console.log("DocumentService.updateDocuments: Ok.")));
  }

  public getDocumentsSpecification(): Observable<any> {
    return this.http
      .get("/api/documents/categories")
      .pipe(tap((_) => console.log("DocumentService.getDocumentsSpecification: Ok.")));
  }

  public getSentDocuments({
    id,
    in_status = undefined,
    categories = [],
    resource_class = "Application",
    section = undefined,
  }): Observable<any> {
    const options = {
      params: {
        in_status,
        "categories[]": categories,
        resource_id: id,
        resource_class,
        section,
      },
    };
    return this.http
      .get("/api/documents/in_status", options)
      .pipe(tap((_) => console.log("DocumentService.getSentDocuments: Ok.")));
  }

  public getDocumentsByStep(step: string | string[], platesProcessId?: string): Observable<any> {
    const options = {
      params: {
        "step[]": step,
        plates_process_id: platesProcessId,
      },
    };
    return this.http
      .get("/api/documents/docs_by_step", options)
      .pipe(tap((_) => console.log("DocumentService.getDocumentsByStep: Ok.")));
  }

  public getRequiredCategories(id: string, resource_class = "Application", additional = undefined): Observable<any> {
    const options = {
      params: {
        resource_id: id,
        resource_class,
        additional,
      },
    };
    return this.http
      .get("/api/documents/", options)
      .pipe(tap((_) => console.log("DocumentService.getRequiredCategories: Ok.")));
  }

  public getAvailableTime(id, quote_id): Observable<any> {
    const options = {
      params: {
        activity_id: id,
        step: "30",
        quote_id,
      },
    };
    return this.http
      .get("/api/users/available_flux_time", options)
      .pipe(tap((_) => console.log("DocumentService.getAvailableTime: Ok.")));
  }

  public markAsDeletedFile(document_id: number | string) {
    return this.http
      .delete(`/api/documents/${document_id}`)
      .pipe(tap((_) => console.log("DocumentService.markAsDeletedFile: Ok.")));
  }

  public bulkDestroy(ids: string[]): Observable<any> {
    const options = {
      params: {
        "ids[]": ids,
      },
    };
    return this.http
      .delete("/api/documents/bulk_destroy", options)
      .pipe(tap((_) => console.log("DocumentService.bulkDestroy: Ok.")));
  }

  public getDocumentAttributes(file) {
    return this.http
      .get(`/api/documents/${file.id}`)
      .pipe(tap((_) => console.log("DocumentService.getDocumentAttributes: Ok.")));
  }

  public getDocumentAiExtractionStatus(id: number | string) {
    return this.http
      .get(`/api/documents/${id}/ai_extraction_status`)
      .pipe(tap((_) => console.log("DocumentService.getDocumentAiExtractionStatus: Ok.")));
  }

  public getDocumentalEvaluationStatus(document_id: number) {
    return this.http
      .get(`/api/documents/${document_id}/documental_evaluation_status`)
      .pipe(tap((_) => console.log("DocumentService.getDocumentalEvaluationStatus: Ok.")));
  }

  public getPurchaseOrderExtractionStatus(id: number | string) {
    return this.http
      .get(`/api/documents/${id}/purchase_order_extraction_status`)
      .pipe(tap((_) => console.log("DocumentService.getPurchaseOrderExtractionStatus: Ok.")));
  }

  /**
   * Marks a REPUVE document as manually overridden (false positive) with a required comment.
   * Sets metadata.openai.repuve.manual_override and creates a repuve_override comment.
   */
  public postRepuveOverride(document_id: number | string, comment: string): Observable<any> {
    return this.http
      .post(`/api/documents/${document_id}/repuve_override`, { comment })
      .pipe(tap((_) => console.log("DocumentService.postRepuveOverride: Ok.")));
  }

  public shareDocuments(resource_class, resource_id, usuario_id) {
    const options = {
      params: {
        usuario_id,
        resource_class,
        resource_id,
      },
    };
    return this.http
      .get("/api/documents/share", options)
      .pipe(tap((_) => console.log("DocumentService.shareDocuments: Ok.")));
  }

  public shareDocument(document_id: string | number, user_id: number) {
    const options = {
      user_id,
    };
    return this.http
      .post(`/api/documents/${document_id}/share_document`, options)
      .pipe(tap((_) => console.log("DocumentService.shareDocument: Ok.")));
  }

  public download(id) {
    return this.http
      .get(`/api/documents/${id}/download`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("DocumentService.download: Ok.")));
  }

  public updateDownpayments(documentId, options: FormData): Observable<any> {
    return this.http
      .put(`/api/documents/${documentId}/update_downpayments`, options)
      .pipe(tap((_) => console.log("DocumentService.updateDownpayments: Ok.")));
  }

  public getByIds(ids): Observable<any> {
    const options = {
      params: {
        "ids[]": ids,
      },
    };
    return this.http
      .get("/api/documents/by_ids", options)
      .pipe(tap((_) => console.log("DocumentService.getByIds: Ok.")));
  }

  public show(id): Observable<any> {
    return this.http.get(`/api/documents/${id}`).pipe(tap((_) => console.log("DocumentService.show: Ok.")));
  }

  public updateFileContent(id, file_content): Observable<any> {
    const options = {
      file_content,
    };
    return this.http
      .put(`/api/documents/${id}/update_content`, options)
      .pipe(tap((_) => console.log("DocumentService.updateFileContent: Ok.")));
  }

  public getDocumentsCount(
    resource_id: any,
    resource_class = "Application",
    category: string = undefined
  ): Observable<any> {
    const options = {
      params: {
        resource_class,
        resource_id,
        category,
      },
    };
    return this.http
      .get("/api/documents/documents_count", options)
      .pipe(tap((_) => console.log("DocumentService.getDocumentsCount: Ok.")));
  }

  public postAddCopies(id, add = [], remove = []) {
    const params = {
      add_ids: add,
      remove_ids: remove,
    };
    return this.http
      .post(`/api/documents/${id}/manage_copies`, params)
      .pipe(tap((_) => console.log("DocumentService.postAddCopies: Ok.")));
  }

  public getUserDocumentCopies(id) {
    return this.http
      .get(`/api/documents/${id}/user_copies`)
      .pipe(tap((_) => console.log("DocumentService.getUserDocumentCopies: Ok.")));
  }

  public postTextractPdf(content): Observable<any> {
    return this.http
      .post("/api/documents/textract_pdf", { content })
      .pipe(tap((_) => console.log("DocumentService.postTextractPdf: Ok.")));
  }

  public getDocumentProofsByCategory(category) {
    const options = {
      params: {
        category,
      },
    };
    return this.http
      .get("/api/documents/document_proofs_by_category", options)
      .pipe(tap((_) => console.log("DocumentService.getDocumentProofsByCategory: Ok.")));
  }

  getDocumentsByCategory(): Observable<any[]> {
    return this.http
      .get("/api/documents/plates_docs_by_category")
      .pipe(tap((_) => console.log("DocumentService.getDocumentsByCategory: Ok.")));
  }

  getSelectedDocuments(platesProcessId: string) {
    return this.http
      .get(`/api/documents/selected_documents?plates_process_id=${platesProcessId}`)
      .pipe(tap((_) => console.log("DocumentService.getSelectedDocuments: Ok.")));
  }

  getAdditionalDocuments(platesProcessId: string) {
    return this.http
      .get(`/api/documents/additional_documents?plates_process_id=${platesProcessId}`)
      .pipe(tap((_) => console.log("DocumentService.getAdditionalDocuments: Ok.")));
  }

  getPhysicalDocuments(platesProcessId: string) {
    return this.http
      .get(`/api/documents/physical_documents?plates_process_id=${platesProcessId}`)
      .pipe(tap((_) => console.log("DocumentService.getPhysicalDocuments: Ok.")));
  }

  public copySelectedDocuments(
    sourceApplicationId: string,
    targetApplicationId: string,
    documentIds: number[]
  ): Observable<any> {
    const options = {
      source_application_id: sourceApplicationId,
      target_application_id: targetApplicationId,
      document_ids: documentIds,
    };
    return this.http
      .post("/api/documents/copy_selected_documents", options)
      .pipe(tap((_) => console.log("DocumentService.copySelectedDocuments: Ok.")));
  }

  public getDocumentalEvaluation(documentId: number): Observable<DocumentalEvaluationResult | null> {
    return this.http
      .get<DocumentalEvaluationResult | null>(`/api/documents/${documentId}/documental_evaluation`)
      .pipe(tap((_) => console.log("DocumentService.getDocumentalEvaluation: Ok.")));
  }

  public saveWarningReport<Warning extends { type: string }>(documentId: number, warning: Warning, comment: string) {
    const params = {
      warning_type: warning.type,
      comment,
      details: warning,
    };
    return this.http
      .post(`/api/documents/${documentId}/warning_reports`, params)
      .pipe(tap((_) => console.log("DocumentService.invalidateWarning: Ok.")));
  }

  public getVerifyBankAllowedDocuments() {
    return this.http
      .get("/api/documents/veryfi_banks_allowed")
      .pipe(tap((_) => console.log("DocumentService.getVerifyBankAllowedDocuments: Ok.")));
  }
}
